<template>
  <div>
    <!-- Mobile Progress Indicator - Simplified for small screens -->
    <div class="md:hidden mb-6">
      <div class="flex items-center justify-between">
        <div class="text-gray-500 text-sm">
          Step {{ currentStep }} of {{ steps.length }}
        </div>
        
        <div class="text-sm">
          <span 
            v-if="currentStep < steps.length" 
            class="text-gray-600"
          >
            {{ steps[currentStep - 1]?.title || `Step ${currentStep}` }}
          </span>
          <span 
            v-else 
            class="text-green-600 font-medium"
          >
            Complete
          </span>
        </div>
      </div>
      
      <!-- Progress Bar -->
      <div class="w-full bg-gray-200 rounded-full h-2.5 mt-2">
        <div 
          class="h-2.5 rounded-full transition-all duration-500 ease-out"
          :class="{ 
            'bg-green-500': currentStep === steps.length, 
            'bg-blue-500': currentStep < steps.length && activeColor === 'blue',
            'bg-green-600': currentStep < steps.length && activeColor === 'green',
            'bg-primary-500': currentStep < steps.length && activeColor === 'primary'
          }"
          :style="{ width: `${(currentStep / steps.length) * 100}%` }"
        ></div>
      </div>
    </div>
  
    <!-- Desktop Progress Indicator - Enhanced with step details -->
    <div class="hidden md:block mb-8">
      <ol class="flex items-center w-full">
        <li 
          v-for="(step, index) in steps" 
          :key="index"
          :class="[
            'flex items-center',
            index < steps.length - 1 ? 'w-full' : ''
          ]"
        >
          <!-- Step Circle -->
          <div 
            :class="[
              'flex items-center justify-center w-10 h-10 rounded-full lg:h-12 lg:w-12 shrink-0',
              getStepClasses(index + 1).circle
            ]"
            :aria-current="currentStep === index + 1 ? 'step' : undefined"
          >
            <span 
              v-if="currentStep > index + 1"
              class="text-white"
              aria-hidden="true"
            >
              <Icon icon="mdi:check" class="w-5 h-5" />
            </span>
            <span 
              v-else
              :class="getStepClasses(index + 1).text"
            >
              {{ index + 1 }}
            </span>
          </div>
          
          <!-- Step Title & Description -->
          <div class="ml-3">
            <h3 
              :class="[
                'text-sm font-medium',
                getStepClasses(index + 1).title
              ]"
            >
              {{ step.title }}
            </h3>
            <p 
              v-if="step.description"
              class="text-xs mt-0.5"
              :class="getStepClasses(index + 1).description"
            >
              {{ step.description }}
            </p>
          </div>
          
          <!-- Connector Line -->
          <div 
            v-if="index < steps.length - 1"
            :class="[
              'flex-1 ml-3 mr-3 h-0.5',
              currentStep > index + 1 ? getStepClasses().connector : 'bg-gray-200'
            ]"
          ></div>
        </li>
      </ol>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import { Icon } from '@iconify/vue';

const props = defineProps({
  // Required - Array of step objects
  steps: {
    type: Array,
    required: true,
    validator: (steps) => {
      return steps.every(step => typeof step.title === 'string');
    }
  },
  
  // Required - Current step number (0 or 1-based)
  currentStep: {
    type: Number,
    required: true,
    validator: (value) => value >= 0
  },
  
  // Color theme for active/completed steps
  activeColor: {
    type: String,
    default: 'blue', // 'blue', 'green', or 'primary'
    validator: (value) => ['blue', 'green', 'primary'].includes(value)
  },
  
  // Text for completed step
  completedText: {
    type: String,
    default: 'Complete'
  }
});

// Create a computed property for step colors
const activeColorMap = computed(() => {
  // Color schemes for each theme
  const colorSchemes = {
    blue: {
      base: 'blue-600',
      light: 'blue-100',
      border: 'blue-500',
      text: 'blue-700',
      description: 'blue-500',
      connector: 'bg-blue-600'
    },
    green: {
      base: 'green-600',
      light: 'green-100',
      border: 'green-500',
      text: 'green-700',
      description: 'green-500',
      connector: 'bg-green-600'
    },
    primary: {
      base: 'primary-600',
      light: 'primary-100',
      border: 'primary-500',
      text: 'primary-700',
      description: 'primary-500',
      connector: 'bg-primary-600'
    }
  };
  
  return colorSchemes[props.activeColor];
});

/**
 * Get classes for a step based on its status (completed, current, upcoming)
 * @param {number} stepNumber - The step number to get classes for
 * @returns {Object} - Object with classes for different parts of the step
 */
const getStepClasses = (stepNumber) => {
  // For connector without step number
  if (!stepNumber) {
    return {
      connector: activeColorMap.value.connector
    };
  }
  
  // Step is completed
  if (props.currentStep > stepNumber) {
    return {
      circle: `bg-${activeColorMap.value.base}`,
      text: 'text-white',
      title: `text-${activeColorMap.value.base}`,
      description: 'text-gray-500',
      connector: activeColorMap.value.connector
    };
  }
  
  // Current step
  if (props.currentStep === stepNumber) {
    return {
      circle: `bg-${activeColorMap.value.light} border-2 border-${activeColorMap.value.border}`,
      text: `text-${activeColorMap.value.text} font-bold`,
      title: `text-${activeColorMap.value.text} font-medium`,
      description: `text-${activeColorMap.value.description}`,
      connector: 'bg-gray-200'
    };
  }
  
  // Future step
  return {
    circle: 'bg-gray-100',
    text: 'text-gray-500',
    title: 'text-gray-500',
    description: 'text-gray-400',
    connector: 'bg-gray-200'
  };
};

// Allow validation of step navigation by returning a check if step exists
const isValidStep = (stepNumber) => {
  return stepNumber > 0 && stepNumber <= props.steps.length;
};

// Expose utility methods
defineExpose({
  isValidStep
});
</script>

<style scoped>
/* Animation for progress bar */
.progress-bar-enter-active,
.progress-bar-leave-active {
  transition: width 0.5s ease-out;
}
</style> 